import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { toast } from "sonner";
import { Users, FileText, DollarSign, Activity, Plus, Edit, Trash2, X, CalendarHeart, Stethoscope, Pill, GraduationCap, Upload, ExternalLink } from "lucide-react";

const CATS = [
  { slug: "menopause", name: "Menopause" }, { slug: "hormones", name: "Hormones" },
  { slug: "metabolic", name: "Metabolic Health" }, { slug: "sleep", name: "Sleep" },
  { slug: "stress", name: "Stress" }, { slug: "glp1", name: "GLP-1 Education" },
];
const EMPTY = { title: "", type: "article", category: "menopause", excerpt: "", body: "", image_url: "", video_url: "", tier: "member", published: true };
const EMPTY_T = { name: "", description: "", price: "", show_price: false, link: "", image_id: null, order: 0 };
const EMPTY_C = { title: "", description: "", price: "", link: "", cta_label: "", image_id: null, featured: false, order: 0 };

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [content, setContent] = useState([]);
  const [members, setMembers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [treatments, setTreatments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [editingT, setEditingT] = useState(null);
  const [editingC, setEditingC] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [mcSettings, setMcSettings] = useState(null);
  const [mcRegs, setMcRegs] = useState([]);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/content").then((r) => setContent(r.data)).catch(() => {});
    api.get("/admin/members").then((r) => setMembers(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/admin/intakes").then((r) => setIntakes(r.data)).catch(() => {});
    api.get("/treatments").then((r) => setTreatments(r.data)).catch(() => {});
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/admin/applications").then((r) => setApplications(r.data)).catch(() => {});
    api.get("/masterclass").then((r) => setMcSettings({ ...r.data, perksText: (r.data.perks || []).join("\n") })).catch(() => {});
    api.get("/admin/masterclass/registrations").then((r) => setMcRegs(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const uploadImage = async (file, apply) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      apply(data.id);
      toast.success("Image uploaded");
    } catch { toast.error("Could not upload image"); }
    finally { setUploading(false); }
  };

  const saveTreatment = async () => {
    if (!editingT.name) { toast.error("Name is required"); return; }
    try {
      if (editingT.id) await api.put(`/admin/treatments/${editingT.id}`, editingT);
      else await api.post("/admin/treatments", editingT);
      toast.success("Saved"); setEditingT(null); load();
    } catch { toast.error("Could not save"); }
  };
  const removeTreatment = async (id) => {
    try { await api.delete(`/admin/treatments/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };
  const saveCourse = async () => {
    if (!editingC.title) { toast.error("Title is required"); return; }
    try {
      if (editingC.id) await api.put(`/admin/courses/${editingC.id}`, editingC);
      else await api.post("/admin/courses", editingC);
      toast.success("Saved"); setEditingC(null); load();
    } catch { toast.error("Could not save"); }
  };
  const removeCourse = async (id) => {
    try { await api.delete(`/admin/courses/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };
  const saveMasterclass = async () => {
    try {
      const perks = (mcSettings.perksText || "").split("\n").map((s) => s.trim()).filter(Boolean);
      await api.put("/admin/masterclass", {
        title: mcSettings.title, subtitle: mcSettings.subtitle, description: mcSettings.description,
        date: mcSettings.date, time: mcSettings.time, perks,
      });
      toast.success("Masterclass updated");
    } catch { toast.error("Could not save"); }
  };

  const save = async () => {
    if (!editing.title || !editing.category) { toast.error("Title and category are required"); return; }
    try {
      if (editing.id) await api.put(`/admin/content/${editing.id}`, editing);
      else await api.post("/admin/content", editing);
      toast.success("Saved");
      setEditing(null); load();
    } catch { toast.error("Could not save"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/content/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const Stat = ({ icon: Icon, value, label, testid }) => (
    <div data-testid={testid} className="bg-white rounded-3xl p-6 border border-taupe">
      <Icon className="h-6 w-6 text-wine mb-3" strokeWidth={1.5} />
      <p className="font-serif text-4xl text-[#3A1230]">{value}</p>
      <p className="text-sm text-[#9B7E8D] mt-1">{label}</p>
    </div>
  );

  const tabs = [
    { k: "overview", l: "Overview" }, { k: "content", l: "Content" },
    { k: "treatments", l: "Treatments" }, { k: "courses", l: "Courses" },
    { k: "applications", l: "Applications" },
    { k: "masterclass", l: "Masterclass" },
    { k: "members", l: "Members" }, { k: "bookings", l: "Bookings" }, { k: "intakes", l: "Intakes" },
  ];

  return (
    <AppLayout>
      <div className="animate-fade-up">
        <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#9B7E8D]">Admin</span>
        <h1 className="mt-2 font-serif text-5xl tracking-tight text-wine">Manage BloomHer</h1>
      </div>

      <div className="mt-8 flex gap-2.5 overflow-x-auto pb-2">
        {tabs.map((t) => (
          <button key={t.k} data-testid={`admin-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-medium transition-all ${tab === t.k ? "bg-wine text-white" : "bg-white border border-taupe text-[#6E5862] hover:bg-cream"}`}>{t.l}</button>
        ))}
      </div>

      {tab === "overview" && stats && (
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-4">
          <Stat testid="admin-stat-members" icon={Users} value={stats.total_members} label="Total members" />
          <Stat testid="admin-stat-subs" icon={DollarSign} value={stats.active_subscriptions} label="Active subscriptions" />
          <Stat testid="admin-stat-mrr" icon={DollarSign} value={`$${stats.mrr}`} label="Est. MRR" />
          <Stat testid="admin-stat-content" icon={FileText} value={stats.total_content} label="Content pieces" />
          <Stat testid="admin-stat-checkins" icon={Activity} value={stats.total_check_ins} label="Total check-ins" />
          <Stat testid="admin-stat-paid" icon={DollarSign} value={stats.paid_transactions} label="Paid transactions" />
        </div>
      )}

      {tab === "content" && (
        <div className="mt-8">
          <button data-testid="admin-add-content" onClick={() => setEditing({ ...EMPTY })} className="inline-flex items-center gap-2 rounded-full bg-wine px-6 py-3 text-sm font-medium text-white hover:bg-wine-dark transition-all">
            <Plus className="h-4 w-4" /> New content
          </button>
          <div className="mt-6 space-y-3">
            {content.map((c) => (
              <div key={c.id} data-testid={`admin-content-${c.id}`} className="flex items-center justify-between bg-white rounded-2xl p-5 border border-taupe">
                <div className="min-w-0">
                  <p className="font-serif text-xl text-[#3A1230] truncate">{c.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs rounded-full bg-[#F7E9EF] px-2 py-0.5 text-wine capitalize">{c.category}</span>
                    <span className="text-xs text-[#9B7E8D]">{c.type} · {c.tier}</span>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button data-testid={`admin-edit-${c.id}`} onClick={() => setEditing(c)} className="p-2 text-[#6E5862] hover:text-wine"><Edit className="h-4 w-4" /></button>
                  <button data-testid={`admin-delete-${c.id}`} onClick={() => remove(c.id)} className="p-2 text-[#6E5862] hover:text-[#9B4444]"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "treatments" && (
        <div className="mt-8">
          <button data-testid="admin-add-treatment" onClick={() => setEditingT({ ...EMPTY_T, order: treatments.length })} className="inline-flex items-center gap-2 rounded-full bg-wine px-6 py-3 text-sm font-medium text-white hover:bg-wine-dark transition-all">
            <Plus className="h-4 w-4" /> New treatment
          </button>
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {treatments.map((t) => (
              <div key={t.id} data-testid={`admin-treatment-${t.id}`} className="flex items-center gap-4 bg-white rounded-2xl p-4 border border-taupe">
                <div className="h-16 w-16 rounded-xl bg-[#F6ECF0] overflow-hidden flex items-center justify-center flex-shrink-0">
                  {t.image_id ? <img src={`${API}/media/${t.image_id}`} alt={t.name} className="h-full w-full object-cover" /> : <Pill className="h-6 w-6 text-[#C9A2B5]" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-serif text-xl text-[#3A1230] truncate">{t.name}</p>
                  <p className="text-xs text-[#9B7E8D] truncate">{t.link ? "Link set" : "No link yet"}{t.show_price && t.price ? ` · ${t.price}` : ""}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button data-testid={`admin-edit-treatment-${t.id}`} onClick={() => setEditingT(t)} className="p-2 text-[#6E5862] hover:text-wine"><Edit className="h-4 w-4" /></button>
                  <button data-testid={`admin-delete-treatment-${t.id}`} onClick={() => removeTreatment(t.id)} className="p-2 text-[#6E5862] hover:text-[#9B4444]"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "courses" && (
        <div className="mt-8">
          <button data-testid="admin-add-course" onClick={() => setEditingC({ ...EMPTY_C, order: courses.length })} className="inline-flex items-center gap-2 rounded-full bg-wine px-6 py-3 text-sm font-medium text-white hover:bg-wine-dark transition-all">
            <Plus className="h-4 w-4" /> New course
          </button>
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {courses.map((c) => (
              <div key={c.id} data-testid={`admin-course-${c.id}`} className="flex items-center gap-4 bg-white rounded-2xl p-4 border border-taupe">
                <div className="h-16 w-16 rounded-xl bg-[#F6ECF0] overflow-hidden flex items-center justify-center flex-shrink-0">
                  {c.image_id ? <img src={`${API}/media/${c.image_id}`} alt={c.title} className="h-full w-full object-cover" /> : <GraduationCap className="h-6 w-6 text-[#C9A2B5]" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-serif text-xl text-[#3A1230] truncate">{c.title} {c.featured && <span className="text-xs text-[#8a6d1f]">★</span>}</p>
                  <p className="text-xs text-[#9B7E8D] truncate">{c.link ? "Link set" : "No link yet"}{c.price ? ` · ${c.price}` : ""}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button data-testid={`admin-edit-course-${c.id}`} onClick={() => setEditingC(c)} className="p-2 text-[#6E5862] hover:text-wine"><Edit className="h-4 w-4" /></button>
                  <button data-testid={`admin-delete-course-${c.id}`} onClick={() => removeCourse(c.id)} className="p-2 text-[#6E5862] hover:text-[#9B4444]"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "applications" && (
        <div className="mt-8 space-y-3">
          {applications.length === 0 && <p className="text-[#9B7E8D]">No applications yet.</p>}
          {applications.map((a) => (
            <div key={a.id} data-testid={`admin-application-${a.id}`} className="bg-white rounded-2xl p-5 border border-taupe">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-serif text-xl text-[#3A1230]">{a.name} <span className="text-sm text-[#9B7E8D]">· {a.email}{a.phone ? ` · ${a.phone}` : ""}</span></p>
                  <p className="text-xs text-[#9B7E8D] mt-0.5">{new Date(a.created_at).toLocaleString()}</p>
                </div>
                <span className={`text-xs rounded-full px-3 py-1 font-medium ${a.type === "fullbloom" ? "bg-[#F7E9EF] text-wine" : "bg-[#EAF0EC] text-[#4A6B53]"}`}>
                  {a.type === "fullbloom" ? "Full Bloom" : "Radiance"}
                </span>
              </div>
              <div className="mt-3 space-y-2 border-t border-taupe pt-3">
                {Object.entries(a.answers || {}).map(([q, ans]) => (
                  <div key={q} className="text-sm">
                    <p className="text-[#9B7E8D]">{q}</p>
                    <p className="text-[#3A1230]">{Array.isArray(ans) ? ans.join(", ") : String(ans)}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "masterclass" && mcSettings && (
        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl p-6 border border-taupe">
            <h3 className="font-serif text-2xl text-[#3A1230] mb-4">Masterclass details</h3>
            <div className="space-y-3">
              <input data-testid="mc-edit-title" value={mcSettings.title || ""} onChange={(e) => setMcSettings({ ...mcSettings, title: e.target.value })} placeholder="Title" className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none" />
              <input data-testid="mc-edit-subtitle" value={mcSettings.subtitle || ""} onChange={(e) => setMcSettings({ ...mcSettings, subtitle: e.target.value })} placeholder="Subtitle" className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none" />
              <textarea data-testid="mc-edit-description" rows={3} value={mcSettings.description || ""} onChange={(e) => setMcSettings({ ...mcSettings, description: e.target.value })} placeholder="Description" className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none resize-none" />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="mc-edit-date" value={mcSettings.date || ""} onChange={(e) => setMcSettings({ ...mcSettings, date: e.target.value })} placeholder="Date (e.g. Thurs, July 10)" className="rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none" />
                <input data-testid="mc-edit-time" value={mcSettings.time || ""} onChange={(e) => setMcSettings({ ...mcSettings, time: e.target.value })} placeholder="Time (e.g. 7pm EST)" className="rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none" />
              </div>
              <textarea data-testid="mc-edit-perks" rows={4} value={mcSettings.perksText || ""} onChange={(e) => setMcSettings({ ...mcSettings, perksText: e.target.value })} placeholder="One perk per line" className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none resize-none" />
              <button data-testid="mc-save" onClick={saveMasterclass} className="w-full rounded-full bg-wine px-6 py-3.5 text-sm font-medium text-white hover:bg-wine-dark transition-all">Save masterclass</button>
              <a href="/masterclass" target="_blank" rel="noreferrer" className="block text-center text-sm text-wine underline">View public registration page</a>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-taupe">
            <h3 className="font-serif text-2xl text-[#3A1230] mb-1">Registrations</h3>
            <p className="text-sm text-[#9B7E8D] mb-4">{mcRegs.length} registered</p>
            <div className="space-y-2 max-h-[480px] overflow-y-auto">
              {mcRegs.length === 0 && <p className="text-sm text-[#9B7E8D]">No registrations yet.</p>}
              {mcRegs.map((r) => (
                <div key={r.id} data-testid={`mc-reg-${r.id}`} className="flex items-center justify-between gap-3 border-b border-taupe pb-2">
                  <div className="min-w-0">
                    <p className="text-[#3A1230] truncate">{r.name}</p>
                    <p className="text-xs text-[#9B7E8D] truncate">{r.email}{r.phone ? ` · ${r.phone}` : ""}</p>
                    {r.session && <p className="text-xs text-wine truncate">{r.session}</p>}
                  </div>
                  <span className="text-xs text-[#9B7E8D] flex-shrink-0">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "members" && (
        <div className="mt-8 bg-white rounded-3xl border border-taupe overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-cream text-left"><tr>
              <th className="px-5 py-3 font-medium text-[#6E5862]">Name</th>
              <th className="px-5 py-3 font-medium text-[#6E5862]">Email</th>
              <th className="px-5 py-3 font-medium text-[#6E5862]">Role</th>
              <th className="px-5 py-3 font-medium text-[#6E5862]">Status</th>
            </tr></thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.user_id} data-testid={`admin-member-${m.user_id}`} className="border-t border-taupe">
                  <td className="px-5 py-3 text-[#3A1230]">{m.name}</td>
                  <td className="px-5 py-3 text-[#6E5862]">{m.email}</td>
                  <td className="px-5 py-3 capitalize text-[#6E5862]">{m.role}</td>
                  <td className="px-5 py-3">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${m.subscription_status === "active" ? "bg-[#EAF0EC] text-[#4A6B53]" : "bg-[#F6ECF0] text-[#9B7E8D]"}`}>{m.subscription_status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "bookings" && (
        <div className="mt-8 space-y-3">
          {bookings.length === 0 && <p className="text-[#9B7E8D]">No coaching bookings yet.</p>}
          {bookings.map((b) => (
            <div key={b.id} className="flex items-center justify-between bg-white rounded-2xl p-5 border border-taupe">
              <div className="flex items-center gap-3">
                <CalendarHeart className="h-5 w-5 text-wine" strokeWidth={1.5} />
                <div>
                  <p className="text-[#3A1230]">{b.user_name || b.user_email} → <span className="font-medium">{b.coach_name}</span></p>
                  <p className="text-xs text-[#9B7E8D]">{b.date} · {b.time}</p>
                </div>
              </div>
              <span className={`text-xs rounded-full px-2.5 py-1 ${b.status === "cancelled" ? "bg-[#F5EBEB] text-[#9B4444]" : "bg-[#EAF0EC] text-[#4A6B53]"}`}>{b.status}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "intakes" && (
        <div className="mt-8 space-y-3">
          {intakes.length === 0 && <p className="text-[#9B7E8D]">No clinical intakes yet.</p>}
          {intakes.map((it) => (
            <div key={it.id} className="bg-white rounded-2xl p-5 border border-taupe">
              <div className="flex items-center gap-3">
                <Stethoscope className="h-5 w-5 text-wine" strokeWidth={1.5} />
                <div>
                  <p className="text-[#3A1230]">{it.user_name || it.user_email} — <span className="font-medium">{it.primary_concern}</span></p>
                  <p className="text-xs text-[#9B7E8D] mt-0.5">{it.reason} · {new Date(it.created_at).toLocaleString()}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Content editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#3A1230]/40 px-5" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-[2rem] p-8 max-w-lg w-full max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-3xl text-wine">{editing.id ? "Edit content" : "New content"}</h2>
              <button data-testid="admin-modal-close" onClick={() => setEditing(null)}><X className="h-5 w-5 text-[#9B7E8D]" /></button>
            </div>
            <div className="space-y-4">
              <input data-testid="content-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Title"
                className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine" />
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="content-type" value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} className="rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none">
                  <option value="article">Article</option><option value="video">Video</option>
                </select>
                <select data-testid="content-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none">
                  {CATS.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              <select data-testid="content-tier" value={editing.tier} onChange={(e) => setEditing({ ...editing, tier: e.target.value })} className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none">
                <option value="member">Members only</option><option value="free">Free (preview)</option>
              </select>
              <input data-testid="content-image" value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} placeholder="Image URL"
                className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine" />
              {editing.type === "video" && (
                <input data-testid="content-video" value={editing.video_url} onChange={(e) => setEditing({ ...editing, video_url: e.target.value })} placeholder="Video embed URL"
                  className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine" />
              )}
              <textarea data-testid="content-excerpt" value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} rows={2} placeholder="Short excerpt"
                className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none resize-none" />
              <textarea data-testid="content-body" value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} rows={6} placeholder="Full body (use blank lines for paragraphs)"
                className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none resize-none" />
              <button data-testid="content-save" onClick={save} className="w-full rounded-full bg-wine px-6 py-3.5 text-sm font-medium text-white hover:bg-wine-dark transition-all">Save content</button>
            </div>
          </div>
        </div>
      )}
      {/* Treatment editor modal */}
      {editingT && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#3A1230]/40 px-5" onClick={() => setEditingT(null)}>
          <div className="bg-white rounded-[2rem] p-8 max-w-lg w-full max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-3xl text-wine">{editingT.id ? "Edit treatment" : "New treatment"}</h2>
              <button data-testid="treatment-modal-close" onClick={() => setEditingT(null)}><X className="h-5 w-5 text-[#9B7E8D]" /></button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-20 w-20 rounded-xl bg-[#F6ECF0] overflow-hidden flex items-center justify-center flex-shrink-0">
                  {editingT.image_id ? <img src={`${API}/media/${editingT.image_id}`} alt="" className="h-full w-full object-cover" /> : <Pill className="h-7 w-7 text-[#C9A2B5]" />}
                </div>
                <label className="inline-flex items-center gap-2 rounded-full border border-wine px-4 py-2.5 text-sm font-medium text-wine hover:bg-[#F6ECF0] cursor-pointer transition-all" data-testid="treatment-upload-label">
                  <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload image"}
                  <input type="file" accept="image/*" data-testid="treatment-image-input" className="hidden" onChange={(e) => uploadImage(e.target.files[0], (id) => setEditingT((p) => ({ ...p, image_id: id })))} />
                </label>
              </div>
              <input data-testid="treatment-name" value={editingT.name} onChange={(e) => setEditingT({ ...editingT, name: e.target.value })} placeholder="Name (e.g. Semaglutide)"
                className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none" />
              <textarea data-testid="treatment-description" value={editingT.description} onChange={(e) => setEditingT({ ...editingT, description: e.target.value })} rows={2} placeholder="Short description"
                className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none resize-none" />
              <input data-testid="treatment-link" value={editingT.link} onChange={(e) => setEditingT({ ...editingT, link: e.target.value })} placeholder="LegUp Rx link (https://…)"
                className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none" />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="treatment-price" value={editingT.price} onChange={(e) => setEditingT({ ...editingT, price: e.target.value })} placeholder="Price (e.g. $199/mo)"
                  className="rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none" />
                <label className="flex items-center gap-2 text-sm text-[#6E5862] px-1">
                  <input type="checkbox" data-testid="treatment-showprice" checked={editingT.show_price} onChange={(e) => setEditingT({ ...editingT, show_price: e.target.checked })} className="h-4 w-4 accent-wine" />
                  Show price on card
                </label>
              </div>
              <button data-testid="treatment-save" onClick={saveTreatment} className="w-full rounded-full bg-wine px-6 py-3.5 text-sm font-medium text-white hover:bg-wine-dark transition-all">Save treatment</button>
            </div>
          </div>
        </div>
      )}

      {/* Course editor modal */}
      {editingC && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#3A1230]/40 px-5" onClick={() => setEditingC(null)}>
          <div className="bg-white rounded-[2rem] p-8 max-w-lg w-full max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-3xl text-wine">{editingC.id ? "Edit course" : "New course"}</h2>
              <button data-testid="course-modal-close" onClick={() => setEditingC(null)}><X className="h-5 w-5 text-[#9B7E8D]" /></button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-20 w-20 rounded-xl bg-[#F6ECF0] overflow-hidden flex items-center justify-center flex-shrink-0">
                  {editingC.image_id ? <img src={`${API}/media/${editingC.image_id}`} alt="" className="h-full w-full object-cover" /> : <GraduationCap className="h-7 w-7 text-[#C9A2B5]" />}
                </div>
                <label className="inline-flex items-center gap-2 rounded-full border border-wine px-4 py-2.5 text-sm font-medium text-wine hover:bg-[#F6ECF0] cursor-pointer transition-all" data-testid="course-upload-label">
                  <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload image"}
                  <input type="file" accept="image/*" data-testid="course-image-input" className="hidden" onChange={(e) => uploadImage(e.target.files[0], (id) => setEditingC((p) => ({ ...p, image_id: id })))} />
                </label>
              </div>
              <input data-testid="course-title" value={editingC.title} onChange={(e) => setEditingC({ ...editingC, title: e.target.value })} placeholder="Course title"
                className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none" />
              <textarea data-testid="course-description" value={editingC.description} onChange={(e) => setEditingC({ ...editingC, description: e.target.value })} rows={3} placeholder="Description"
                className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none resize-none" />
              <input data-testid="course-link" value={editingC.link} onChange={(e) => setEditingC({ ...editingC, link: e.target.value })} placeholder="Link (Kajabi URL, or /apply/radiance for application)"
                className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none" />
              <input data-testid="course-cta" value={editingC.cta_label} onChange={(e) => setEditingC({ ...editingC, cta_label: e.target.value })} placeholder="Button label (e.g. Reserve Your Seat, Apply for Radiance)"
                className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none" />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="course-price" value={editingC.price} onChange={(e) => setEditingC({ ...editingC, price: e.target.value })} placeholder="Cost (e.g. $497)"
                  className="rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none" />
                <label className="flex items-center gap-2 text-sm text-[#6E5862] px-1">
                  <input type="checkbox" data-testid="course-featured" checked={editingC.featured} onChange={(e) => setEditingC({ ...editingC, featured: e.target.checked })} className="h-4 w-4 accent-wine" />
                  Feature as bundle
                </label>
              </div>
              <button data-testid="course-save" onClick={saveCourse} className="w-full rounded-full bg-wine px-6 py-3.5 text-sm font-medium text-white hover:bg-wine-dark transition-all">Save course</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
